/* util/poi-nearest.js — pure helpers för "Analysera läget"-featuren.
 *
 * Inga DOM- eller MapLibre-beroenden. Allt är rena funktioner som kan
 * användas och testas isolerat. Beroende: state/commute-data.js för bil-
 * och kollektiv-matriserna (snap-till-tätort + uppslag).
 *
 * Distance → time-modeller (hybrid: matris + haversine):
 *   - Gång: 5 km/h × omvägs-faktor 1.3 (svensk gata-faktor)
 *   - Cykel: 15 km/h × 1.3
 *   - Bil: snappa pin + POI till närmsta tätort. Samma tätort eller
 *     fågelavstånd < 3 km → stadsintern haversine × 1.3 / 40 km/h.
 *     Annars carMin(fromI, toI) från car-matrisen. Fallback om matris
 *     saknas: haversine × 1.3 / 60 km/h.
 *   - Kollektivt: samma snap-strategi. Stadsintern fallback:
 *     haversine × 1.3 / 12 km/h. Annars commuteMin(fromI, toI).
 *     Unreachable (0xFFFF) → null (UI renderar "—").
 *
 * Varför stadsintern fallback krävs: matrisens diagonal är 0 minuter,
 * och förskola/matbutik ligger ofta i samma tätort som pinnen.
 */

import {
    nearestTatort,
    commuteMin,
    carMin,
    isCarLoaded,
    isLoaded as isTransitLoaded,
} from '../state/commute-data.js';

const EARTH_R_KM = 6371.0088;
const DETOUR = 1.3;                 // fågelväg → riktig väg
const URBAN_THRESHOLD_KM = 3;       // under detta → stadsintern fallback
const WALK_KMH = 5;
const BIKE_KMH = 15;
const URBAN_CAR_KMH = 40;
const URBAN_TRANSIT_KMH = 12;
const FALLBACK_CAR_KMH = 60;

export function haversineKm(lat1, lon1, lat2, lon2) {
    const p1 = lat1 * Math.PI / 180;
    const p2 = lat2 * Math.PI / 180;
    const dp = (lat2 - lat1) * Math.PI / 180;
    const dl = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    return 2 * EARTH_R_KM * Math.asin(Math.sqrt(a));
}

/**
 * Hitta närmaste POI till en punkt via min(haversine). Brute-force men snabbt:
 * 5321 features × ~50ns per haversine ≈ < 1 ms.
 *
 * Returnerar { feature, distKm } eller null om features är tomt.
 */
export function nearestPoi(features, lat, lon) {
    if (!features || features.length === 0) return null;
    let best = null;
    let bestDist = Infinity;
    for (const f of features) {
        if (!f || !f.geometry || f.geometry.type !== 'Point') continue;
        const c = f.geometry.coordinates;
        if (!c || c.length < 2) continue;
        const d = haversineKm(lat, lon, c[1], c[0]);
        if (d < bestDist) { bestDist = d; best = f; }
    }
    return best ? { feature: best, distKm: bestDist } : null;
}

export function walkMin(distKm) {
    if (!Number.isFinite(distKm)) return null;
    return (distKm * DETOUR) / WALK_KMH * 60;
}

export function bikeMin(distKm) {
    if (!Number.isFinite(distKm)) return null;
    return (distKm * DETOUR) / BIKE_KMH * 60;
}

/**
 * Snappar både pin och POI till närmsta tätort. Returnerar { fromI, toI,
 * sameTatort } eller null om någon snap failar (origins inte laddad).
 */
function snapPair(pinLat, pinLon, poiLat, poiLon) {
    const from = nearestTatort(pinLat, pinLon);
    const to   = nearestTatort(poiLat, poiLon);
    if (!from || !to) return null;
    return {
        fromI: from.origin.i,
        toI:   to.origin.i,
        sameTatort: from.origin.i === to.origin.i,
    };
}

/**
 * Bil-restid (minuter) från pin till POI.
 *
 * Algoritm:
 *   1. Om pin/POI ligger i samma tätort ELLER fågel < 3 km → stadsintern
 *      (haversine × 1.3 / 40 km/h). Matrisens diagonal är 0 minuter och
 *      kan inte stoppa "samma kommun, samma stad"-fall korrekt.
 *   2. Annars: snappa till närmsta tätort + slå upp carMin(fromI, toI).
 *   3. Fallback om car-matris saknas: haversine × 1.3 / 60 km/h.
 */
export function carTimeMin(pinLat, pinLon, poiLat, poiLon, distKm) {
    if (!Number.isFinite(distKm)) return null;
    if (distKm < URBAN_THRESHOLD_KM) {
        return (distKm * DETOUR) / URBAN_CAR_KMH * 60;
    }
    if (!isCarLoaded()) {
        return (distKm * DETOUR) / FALLBACK_CAR_KMH * 60;
    }
    const snap = snapPair(pinLat, pinLon, poiLat, poiLon);
    if (!snap) {
        return (distKm * DETOUR) / FALLBACK_CAR_KMH * 60;
    }
    if (snap.sameTatort) {
        return (distKm * DETOUR) / URBAN_CAR_KMH * 60;
    }
    const v = carMin(snap.fromI, snap.toI);
    if (v == null) {
        return (distKm * DETOUR) / FALLBACK_CAR_KMH * 60;
    }
    return v;
}

/**
 * Kollektiv-restid (minuter) från pin till POI.
 *
 * Algoritm:
 *   1. Stadsintern (samma tätort eller fågel < 3 km) → haversine × 1.3 / 12.
 *      Kollektivt i staden är typiskt buss med stops, ~12 km/h snitt.
 *   2. Annars snappa + commuteMin(fromI, toI). Returnerar null om
 *      unreachable så UI renderar "—" istället för 0.
 */
export function transitTimeMin(pinLat, pinLon, poiLat, poiLon, distKm) {
    if (!Number.isFinite(distKm)) return null;
    if (distKm < URBAN_THRESHOLD_KM) {
        return (distKm * DETOUR) / URBAN_TRANSIT_KMH * 60;
    }
    if (!isTransitLoaded()) return null;
    const snap = snapPair(pinLat, pinLon, poiLat, poiLon);
    if (!snap) return null;
    if (snap.sameTatort) {
        return (distKm * DETOUR) / URBAN_TRANSIT_KMH * 60;
    }
    return commuteMin(snap.fromI, snap.toI);  // null vid unreachable
}

/* --- formatters ---------------------------------------------------------- */

export function fmtDistance(distKm) {
    if (!Number.isFinite(distKm)) return '—';
    if (distKm < 1) return Math.round(distKm * 1000) + ' m';
    return distKm.toFixed(1) + ' km';
}

export function fmtMin(min) {
    if (min == null || !Number.isFinite(min)) return '—';
    const m = Math.max(1, Math.round(min));
    if (m < 60) return m + ' min';
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem === 0 ? h + ' h' : h + ' h ' + rem + ' min';
}
